<template>
  <div
    class="quote-card"
    :class="{
      selected: item.included,
      'not-clickable': !isClickable
    }"
    @touchstart="startTouch($event)"
    @touchmove="moveTouch($event)"
    @touchend="endTouch"
    @touchcancel="touchCanceled"
  >
    <div class="row d-flex flex-nowrap">
      <div class="mr-1">
        <div class="quote-id text-muted small mb-1">
          <a
            class="dark-link small"
            style="min-width: 25%"
            :href="'https://admin' + webappUrl + '/pending-items/'+ item.id + '/edit'" target="_blank">
            #{{ item.id }}<i class="fa fa-external-link-alt ml-1"></i>
          </a>
        </div>
        <img
          width="60"
          :src="item.attributes.images && item.attributes.images.length > 0 ? item.attributes.images[0].url : placeholderImgUrl"
        />
        {{ formatDate(item.attributes.createdAt) }}<br>
      </div>
      <div style="min-width: 50%">
        <p class="brand text-gray font-bold mrgin0Px-bottom">
          {{ item.attributes.brand }}
          <span class="small"> {{ item.attributes.name }}</span>
        </p>
        <div v-if="item.attributes.hasPurchaseOffer && item.attributes.isApproved">
          <div
            v-if="item.attributes.offers.consignmentEstimate"
            class="strong mt-2"
          >
            Consign: ${{ item.attributes.offers.consignmentEstimate }}
          </div>
          <div
            v-if="item.attributes.offers.buyoutQuote"
            class="strong mt-2"
          >
            Buyout: ${{ item.attributes.offers.buyoutQuote }}
          </div>
          <div
            v-if="item.attributes.offers.buybackQuote"
            class="strong mt-2"
          >
            Buyout: ${{ item.attributes.offers.buybackQuote }}
          </div>
        </div>
        <div v-if="item.attributes.authenticationStatus" class="quote-status-indicator mt-4">
          {{ item.attributes.authenticationStatus }}
        </div>
      </div>
      <div class="text-right">
        <div v-if="isStatusActionable()" class="quote-status-indicator mb-2 cursor-pointer" @click="handleClick">
          Select
          <i
            v-if="item.included"
            class="fa fa-check-circle text-success"
            aria-hidden="true"></i>
        </div>
        <div class="quote-status-indicator mb-2 cursor-pointer" @click="viewDetails">
          INFO
          <i
            class="fa fa-info-circle"
            aria-hidden="true"></i>
        </div>
        <div class="quote-status-indicator" :class="getQuoteStatusClass">
          {{ getQuoteStatusText }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { MIN_DESKTOP_WIDTH, KNOWN_STATUS_CLASSES } from '@/constants'
import dark from '@/router/layouts/dark.vue'
export default {
  name: 'QuoteCard',
  props: {
    item: {
      type: Object,
      required: true
    },
    placeholderImgUrl: {
      type: String,
      required: true
    },
    isFinishedAddingItems: {
      type: Boolean,
      default: false
    },
    isClickable: {
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      touchStartY: 0,
      touchMoved: false,
      actionableStatuses: [
        'Accepted'
      ]
    }
  },
  computed: {
    dark () {
      return dark
    },
    getQuoteStatusText () {
      const attrs = this.item.attributes

      // Check if item has been picked up (pickup_type is "concierge")
      if (attrs.pickup_type === 'concierge') {
        return 'Picked Up'
      }

      // Use the status key from the quotes response
      if (attrs.status) {
        // Add spaces before capital letters (camelCase to readable text)
        return attrs.status.replace(/([a-z])([A-Z])/g, '$1 $2')
      }

      // Fallback for backwards compatibility
      return 'Not Submitted'
    },
    getQuoteStatusClass () {
      const attrs = this.item.attributes

      // Check if item has been picked up (pickup_type is "concierge")
      if (attrs.pickup_type === 'concierge') {
        return 'status-picked-up'
      }

      // Use the status key from the quotes response
      if (attrs.status) {
        // Map the new API statuses to CSS classes
        const statusClassMap = {
          pending_authentication: 'status-pending-authentication',
          authentication_in_progress: 'status-authentication-in-progress',
          authenticated_no_offer: 'status-authenticated-no-offer',
          authenticated_with_offer: 'status-authenticated-with-offer',
          offer_pending: 'status-offer-pending',
          offer_accepted: 'status-offer-accepted',
          in_transit: 'status-in-transit',
          received: 'status-received-at-warehouse',
          converted: 'status-converted-to-product',
          fake: 'status-counterfeit-detected',
          needs_more_info: 'status-needs-more-information'
        }

        // Return mapped status class or fallback to generic formatting
        if (statusClassMap[attrs.status]) {
          return statusClassMap[attrs.status]
        }

        // Fallback for legacy statuses - add spaces before capital letters, then convert to CSS class format
        const statusClass = attrs.status
          .replace(/([a-z])([A-Z])/g, '$1 $2') // Add spaces before capitals
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '')

        const className = `status-${statusClass}`

        return KNOWN_STATUS_CLASSES.includes(className) ? className : 'status-unknown'
      }

      return 'status-not-submitted'
    },
    webappUrl () {
      return process.env.VUE_APP_FASHIONPHILE_DOMAIN || ''
    }
  },
  methods: {
    handleClick () {
      if (this.isClickable && window.innerWidth > MIN_DESKTOP_WIDTH) {
        this.toggleIncluded()
      }
    },
    startTouch (event) {
      this.touchStartY = event.touches[0].clientY
      this.touchMoved = false
    },
    moveTouch (event) {
      if (Math.abs(event.touches[0].clientY - this.touchStartY) > 10) {
        this.touchMoved = true
      }
    },
    endTouch () {
      if (this.isClickable && !this.touchMoved) {
        this.toggleIncluded()
      }
    },
    touchCanceled () {
      // Reset touch state
      this.touchMoved = false
    },
    toggleIncluded () {
      if (!this.isFinishedAddingItems) {
        this.$emit('toggle-included', this.item)
      }
    },
    viewDetails () {
      this.$emit('view-details', this.item)
    },
    formatDate (date) {
      const formattedDate = new Date(date)

      return (
        formattedDate.getMonth() +
        1 +
        '/' +
        formattedDate.getDate() +
        '/' +
        formattedDate.getFullYear()
      )
    },
    isStatusActionable () {
      return this.actionableStatuses.includes(this.item.attributes.status)
    }
  }
}
</script>

<style scoped>
/* Quote ID styling */
.quote-id {
  font-family: 'Courier New', monospace;
  font-size: 11px;
  color: #6c757d;
  font-weight: 500;
  letter-spacing: 0.5px;
}

/* Status badge modern pill */
.quote-status-indicator {
  padding: 4px 8px;
  border-radius: 999px;
  font-size: 8px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  backdrop-filter: saturate(1.1) blur(1px);
  white-space: nowrap;
  display: inline-block;
}

/* Status-specific styling */
.status-picked-up {
  background-color: #28a745;
  color: white;
  font-weight: 700;
  box-shadow: 0 2px 4px rgba(40, 167, 69, 0.3);
}

/* Card modern layout */
.quote-card {
  position: relative;
  display: flex;
  gap: 12px;
  padding: 12px;
  border-radius: 12px;
  background: #fff;
  border: 1px solid rgba(0,0,0,0.06);
  transition: transform 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease, border-color 0.2s ease;
}

@media (hover: hover) {
  .quote-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0,0,0,0.08);
    border-color: rgba(0,0,0,0.12);
    background-color: #fafafa;
  }
}

.quote-card.selected {
  box-shadow: 0 8px 24px rgba(34, 197, 94, 0.25);
  background-color: rgba(34, 197, 94, 0.06);
  border: 1px solid rgba(34, 197, 94, 0.5);
}

/* Thumbnail presentation */
.quote-card img {
  width: 60px;
  height: 60px;
  object-fit: cover;
  border-radius: 10px;
  background-color: #f3f4f6;
  border: 1px solid rgba(0,0,0,0.06);
}

/* Text styles */
.brand {
  font-size: 14px;
  line-height: 14px;
}

.quote-name {
  color: #495057;
  margin: 0 0 6px 0;
  line-height: 1.3;
}

/* Not clickable state */
.quote-card.not-clickable {
  background: #f3f4f6 !important;
  opacity: 0.7;
  cursor: not-allowed;
  pointer-events: none;
}

@media (hover: hover) {
  .quote-card.not-clickable:hover {
    transform: none;
    box-shadow: none;
    background: #f3f4f6 !important;
  }
}

/* Override Bootstrap row margins within quote cards */
.quote-card .row {
  margin: 0 !important;
}

/* Quote Status Indicators */
/* Status Colors - Modern Professional Palette */

/* Default/Fallback */
.status-not-submitted {
  background-color: #6b7280;
  color: white;
}

/* Testing/Development States */
.status-fake {
  background-color: #dc2626;
  color: white;
}

/* Response/Communication States */
.status-responded {
  background-color: #0891b2;
  color: white;
}

.status-response-received {
  background-color: #0891b2;
  color: white;
}

.status-submitted {
  background-color: #2563eb;
  color: white;
}

.status-quote-submitted {
  background-color: #2563eb;
  color: white;
}

/* Pending/Waiting States */
.status-pending {
  background-color: #ea580c;
  color: white;
}

.status-pending-review {
  background-color: #ea580c;
  color: white;
}

.status-awaiting-response {
  background-color: #ca8a04;
  color: white;
}

.status-waiting {
  background-color: #ea580c;
  color: white;
}

/* Information/Action Needed */
.status-needs-info,
.status-needs-more-info {
  background-color: #ca8a04;
  color: white;
}

.status-additional-info-requested {
  background-color: #ca8a04;
  color: white;
}

.status-more-info-needed {
  background-color: #ca8a04;
  color: white;
}

/* Offer States */
.status-offer-made {
  background-color: #0891b2;
  color: white;
}

.status-offer-pending {
  background-color: #7c3aed;
  color: white;
}

.status-offer-sent {
  background-color: #0891b2;
  color: white;
}

.status-quote-sent {
  background-color: #0891b2;
  color: white;
}

/* Buyout States */
.status-buyout-offer,
.status-buyout {
  background-color: #059669;
  color: white;
}

.status-buyout-made {
  background-color: #059669;
  color: white;
}

.status-buyout-sent {
  background-color: #059669;
  color: white;
}

/* Consignment States */
.status-consignment-offer,
.status-consignment {
  background-color: #7c3aed;
  color: white;
}

.status-consignment-made {
  background-color: #7c3aed;
  color: white;
}

.status-consignment-sent {
  background-color: #7c3aed;
  color: white;
}

/* Buyback States */
.status-buyback-offer,
.status-buyback {
  background-color: #0d9488;
  color: white;
}

.status-buyback-made {
  background-color: #0d9488;
  color: white;
}

.status-buyback-sent {
  background-color: #0d9488;
  color: white;
}

/* Success/Positive States */
.status-accepted {
  background-color: #059669;
  color: white;
  border: 1px solid #047857;
}

.status-approved {
  background-color: #059669;
  color: white;
}

.status-completed {
  background-color: #047857;
  color: white;
}

.status-confirmed {
  background-color: #059669;
  color: white;
}

.status-finalized {
  background-color: #047857;
  color: white;
}

/* Negative States */
.status-rejected {
  background-color: #dc2626;
  color: white;
}

.status-declined {
  background-color: #dc2626;
  color: white;
}

.status-cancelled {
  background-color: #6b7280;
  color: white;
}

.status-expired {
  background-color: #6b7280;
  color: white;
}

/* In Progress States */
.status-in-review {
  background-color: #2563eb;
  color: white;
}

.status-processing {
  background-color: #2563eb;
  color: white;
}

.status-under-review {
  background-color: #2563eb;
  color: white;
}

.status-reviewing {
  background-color: #2563eb;
  color: white;
}

.status-being-reviewed {
  background-color: #2563eb;
  color: white;
}

/* New API Status Classes */
.status-pending-authentication {
  background-color: #ca8a04;
  color: white;
}

.status-authentication-in-progress {
  background-color: #2563eb;
  color: white;
}

.status-authenticated-no-offer {
  background-color: #ea580c;
  color: white;
}

.status-authenticated-with-offer {
  background-color: #059669;
  color: white;
}

.status-offer-pending {
  background-color: #7c3aed;
  color: white;
}

.status-offer-accepted {
  background-color: #059669;
  color: white;
}

.status-in-transit {
  background-color: #0891b2;
  color: white;
}

.status-received-at-warehouse {
  background-color: #059669;
  color: white;
}

.status-converted-to-product {
  background-color: #047857;
  color: white;
}

.status-counterfeit-detected {
  background-color: #dc2626;
  color: white;
}

.status-needs-more-information {
  background-color: #ca8a04;
  color: white;
}

/* Special Combined Status */
.status-buyout-consignment {
  background: linear-gradient(45deg, #059669 50%, #7c3aed 50%);
  color: white;
  font-size: 9px;
}

.status-buyout-and-consignment {
  background: linear-gradient(45deg, #059669 50%, #7c3aed 50%);
  color: white;
  font-size: 9px;
}

/* Catch-all for any unknown status classes */
.status-unknown {
  background-color: #6b7280;
  color: white;
}

/* New status for shipped */
.status-shipped {
  background-color: #0891b2;
  color: white;
}
</style>
